import { HStack, Text, useColorModeValue, VStack } from "@chakra-ui/react";
import {
  Pagination,
  PaginationContainer,
  PaginationNext,
  PaginationPage,
  PaginationPageGroup,
  PaginationPrevious,
  PaginationSeparator,
  usePagination,
} from "@ajna/pagination";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import CircularLoader from "../Loader/CircularLoader";

import clamp from "lodash/clamp";
import { useDefaultStyle } from "hooks";

export default function Paginator(props /*: PaginatorProps*/) {
  const { isLoading, totalCount, limit, page, onPageChange, ...xprops } = props;

  const { shadow, borderColor } = useDefaultStyle();

  const color = useColorModeValue("primary.800", "secondary.500");
  const sepBorderColor = useColorModeValue("primary.700", "secondary.700");

  const { pages, pagesCount, offset, currentPage, setCurrentPage, isDisabled, pageSize } = usePagination({
    total: totalCount,
    limits: {
      outer: 1,
      inner: 1,
    },
    initialState: {
      pageSize: limit,
      isDisabled: false,
      currentPage: page,
    },
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
    onPageChange && onPageChange(page);
  };

  return (
    <VStack p={{ base: "8px 0", md: "20px 0" }} justifyContent="space-between" w="100%" {...xprops}>
      {totalCount > 0 && (
        <HStack>
          <Text fontSize={{ base: "14px", "1sm": "16px" }}>
            Showing {offset + 1} to {clamp(offset + pageSize, Math.min(totalCount, pageSize), totalCount ?? 0)} of {totalCount ?? 0}
          </Text>

          {isLoading && <CircularLoader isIndeterminate />}
        </HStack>
      )}

      <HStack mt="0 !important" p="0" gap="0">
        <Pagination pagesCount={pagesCount} currentPage={currentPage} isDisabled={isDisabled} onPageChange={handlePageChange}>
          <PaginationContainer align="center" justify="space-between" p={{ base: 2, md: 4 }} w="full">
            <PaginationPrevious
              mr="8px"
              minH="30px"
              minW="30px"
              maxW="unset"
              maxH="unset"
              boxSize="30px"
              p="0"
              color={color}
              borderRadius="6px"
              _focus={{
                borderColor,
                shadow,
              }}
              _active={{ bg: "transparent" }}
              _hover={{
                bg: "transparent",
              }}
              bg="transparent"
            >
              <ChevronLeftIcon />
            </PaginationPrevious>
            <PaginationPageGroup
              direction="row"
              align="center"
              separator={
                <PaginationSeparator
                  minH="30px"
                  minW="30px"
                  maxW="unset"
                  maxH="unset"
                  border="1px solid transparent"
                  bg="transparent"
                  borderColor={sepBorderColor}
                  borderRadius="6px"
                  color={color}
                  fontSize="sm"
                  boxSize="30px"
                  p="0"
                  jumpSize={4}
                  _active={{ bg: "transparent" }}
                  _hover={{
                    bg: "transparent",
                  }}
                  _focus={{
                    borderColor,
                    shadow,
                  }}
                />
              }
            >
              {pages.map((page) => (
                <PaginationPage
                  minH="30px"
                  minW="30px"
                  maxW="unset"
                  maxH="unset"
                  boxSize="20px"
                  bg="transparent"
                  border="1px solid transparent"
                  borderRadius="6px"
                  color={color}
                  borderColor={sepBorderColor}
                  key={`pagination_page_${page || Math.random() * 7676767665453}`}
                  page={page}
                  p="0"
                  fontSize="sm"
                  _active={{ bg: "transparent" }}
                  _hover={{
                    bg: "transparent",
                  }}
                  _focus={{
                    borderColor,
                    shadow,
                  }}
                  _current={{
                    borderColor: "transparent",
                    bg: sepBorderColor,
                    fontSize: "sm",
                    minW: "30px !important",
                    minH: "30px !important",
                    color: "white",
                    _hover: {
                      borderColor: sepBorderColor,
                      bg: "transparent",
                      color: "black",
                    },
                  }}
                />
              ))}
            </PaginationPageGroup>

            <PaginationNext
              ml="8px"
              minH="30px"
              minW="30px"
              maxW="unset"
              maxH="unset"
              boxSize="30px"
              p="0"
              color={color}
              borderRadius="6px"
              _active={{ bg: "transparent" }}
              _focus={{
                borderColor,
                shadow,
              }}
              _hover={{
                bg: "transparent",
              }}
              bg="transparent"
            >
              <ChevronRightIcon />
            </PaginationNext>
          </PaginationContainer>
        </Pagination>
      </HStack>
    </VStack>
  );
}
